import React from "react";

import { Media } from "../../contexts/media/MediaContext";
import useFeatureToggle from "../../hooks/use-feature-toggle/useFeatureToggle";
import { FeatureToggle, TableContent } from "../../models/app-data-model";
import TableHeading from "./table-heading/table-heading";
import TableRow from "./table-row/table-row";
import "./table.scss";

export type TableProps = {
  content: TableContent;
  featureToggles: FeatureToggle[];
};

export const tableLayout = {
  centered: "Centered",
  wide: "Wide"
};

const Table: React.FC<TableProps> = ({ content, featureToggles }: TableProps) => {
  const useTableComponent: boolean = useFeatureToggle("table-component", featureToggles);

  if (!useTableComponent) {
    return null;
  }

  const totalRows = content?.column?.[0].row?.length || 0;
  const tableRows = [];

  // Function to check if last of row of first column qualifies as a footnote
  const checkIfFootNote = (tableRow: any[]): boolean => {
    if (!tableRow || tableRow.length === 0) return false;

    // Check if the first object has the `cell` property
    const firstObjHasCellProp = tableRow.slice(0, 1).every((value) => value.hasOwnProperty("cell"));

    // Check if all other objects have the `cell` property
    const allOtherObjsHaveCellProp = tableRow.slice(1).every((value) => value.hasOwnProperty("cell"));

    return firstObjHasCellProp && !allOtherObjsHaveCellProp;
  };

  for (let i = 0; i < totalRows; i++) {
    let tableRow = content?.column?.map((column) => {
      return { ...column?.row?.[i], highlight_column: column?.highlight_column };
    });

    const isFootNoteRow = checkIfFootNote(tableRow);

    //check if its the last row to determine if it can be a footNote
    if (i === totalRows - 1 && isFootNoteRow) {
      tableRow = tableRow?.slice(0, 1);
      tableRow = (tableRow ?? []).map((cell) => ({ ...cell, isFootNote: true }));
    }

    tableRows.push(tableRow);
  }

  const tableRowRendering = tableRows?.map((row, index) => {
    const rowDetails = {
      footNote: row[0].isFootNote,
      noOfRows: content?.column?.length || 0
    };
    return <TableRow row={row} key={index} rowDetails={rowDetails} />;
  });

  const tableHeadingRendering = content?.column?.map((column, index) => <TableHeading heading={column?.column_heading} key={index} />);

  return (
    <>
      <Media greaterThanOrEqual="desktop">
        <div className="table-container desktop-table">
          <table className={content.layout === tableLayout.centered ? "centered" : "wide"}>
            <tr>{tableHeadingRendering}</tr>
            {tableRowRendering}
          </table>
        </div>
      </Media>

      <Media lessThan="desktop">
        <div className="table-container mobile-table">
          <table className={content.first_column_sticky ? "first-column-sticky" : ""}>
            <tr>{tableHeadingRendering}</tr>
            {tableRowRendering}
          </table>
        </div>
      </Media>
    </>
  );
};

export default Table;
